import type { CreateTicketTemplateInput, UpdateTicketTemplateInput } from "./tickets-mutations";

export type TicketTemplateHooks = {
  useTemplates: (params?: { includeInactive?: boolean }) => ReturnType<typeof import("./tickets").useTicketTemplates>;
  useCreateTemplate: () => {
    mutate: (input: CreateTicketTemplateInput) => Promise<unknown>;
    isLoading: boolean;
    error: Error | null;
    isError: boolean;
    isSuccess: boolean;
  };
  useUpdateTemplate: () => {
    mutate: (input: UpdateTicketTemplateInput) => Promise<unknown>;
    isLoading: boolean;
    error: Error | null;
    isError: boolean;
    isSuccess: boolean;
  };
  useDeleteTemplate: () => {
    mutate: (id: string) => Promise<unknown>;
    isLoading: boolean;
    error: Error | null;
    isError: boolean;
    isSuccess: boolean;
  };
  useToggleTemplateActive: () => {
    mutate: (input: { id: string; is_active: boolean }) => Promise<unknown>;
    isLoading: boolean;
    error: Error | null;
    isError: boolean;
    isSuccess: boolean;
  };
};

export function makeTicketTemplateHooks(): TicketTemplateHooks {
  return {
    useTemplates: (params?: { includeInactive?: boolean }) => {
      const { useTicketTemplates } = require("./tickets") as typeof import("./tickets");
      return useTicketTemplates(params);
    },

    useCreateTemplate: () => {
      const { useCreateTicketTemplate } = require("./tickets") as typeof import("./tickets");
      const mutation = useCreateTicketTemplate();
      return {
        mutate: async (input: CreateTicketTemplateInput) => mutation.mutateAsync(input),
        isLoading: mutation.isPending,
        error: mutation.error,
        isError: !!mutation.error,
        isSuccess: !mutation.isPending && !mutation.error,
      };
    },

    useUpdateTemplate: () => {
      const { useUpdateTicketTemplate } = require("./tickets") as typeof import("./tickets");
      const mutation = useUpdateTicketTemplate();
      return {
        mutate: async (input: UpdateTicketTemplateInput) => mutation.mutateAsync(input),
        isLoading: mutation.isPending,
        error: mutation.error,
        isError: !!mutation.error,
        isSuccess: !mutation.isPending && !mutation.error,
      };
    },

    useDeleteTemplate: () => {
      const { useDeleteTicketTemplate } = require("./tickets") as typeof import("./tickets");
      const mutation = useDeleteTicketTemplate();
      return {
        mutate: async (id: string) => mutation.mutateAsync(id),
        isLoading: mutation.isPending,
        error: mutation.error,
        isError: !!mutation.error,
        isSuccess: !mutation.isPending && !mutation.error,
      };
    },

    useToggleTemplateActive: () => {
      const { useToggleTicketTemplateActive } = require("./tickets") as typeof import("./tickets");
      const mutation = useToggleTicketTemplateActive();
      return {
        mutate: async ({ id, is_active }: { id: string; is_active: boolean }) =>
          mutation.mutateAsync({ id, is_active }),
        isLoading: mutation.isPending,
        error: mutation.error,
        isError: !!mutation.error,
        isSuccess: !mutation.isPending && !mutation.error,
      };
    },
  };
}
